import { useParams } from 'react-router-dom'
import { handleFormError } from '../../../utils/handleFormError'
import { TextInput } from '../../../components/FormUtils/TextInput'
import { useForm } from 'react-hook-form'
import { useAuth } from 'context/authentication/useAuth'
import to from 'await-to-js'
import { useState } from 'react'
import FbButton from '../../../components/FbUI/FbButton'
import { FbLink } from 'components/FbUI/FbLink'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
} from 'react-icons/hi'

interface ApiError extends Error {
  status?: number
}

const resetPasswordConfirmFormShape = z
  .object({
    password1: z
      .string()
      .min(8, { message: 'Password must contain at least 8 characters' }),
    password2: z
      .string()
      .min(8, { message: 'Password must contain at least 8 characters' }),
  })
  .refine(
    (data) => {
      // Only validate if both fields have values
      if (data.password1 && data.password2) {
        return data.password1 === data.password2
      }
      return true
    },
    {
      message: "Passwords don't match",
      path: ['password2'],
    }
  )

function ResetPasswordConfirm() {
  const { resetPasswordConfirm } = useAuth()
  const [isSuccess, setIsSuccess] = useState(false)
  const [isTokenError, setIsTokenError] = useState(false)
  const urlParams = useParams()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
  } = useForm({
    defaultValues: {
      non_field_errors: '',
      password1: '',
      password2: '',
    },
    resolver: zodResolver(resetPasswordConfirmFormShape),
    mode: 'all',
  })

  const onSubmit = handleSubmit(
    async (values) => {
      setIsTokenError(false)

      if (!urlParams?.uid || !urlParams?.token)
        return setError('non_field_errors', { message: 'Invalid URL' })

      const [err, res] = await to(
        resetPasswordConfirm(
          urlParams.uid,
          urlParams.token,
          values.password1,
          values.password2
        )
      )
      if ((err as ApiError)?.status === 400) {
        setIsTokenError(true)
        return
      }
      if (err) {
        handleFormError(err, setError)
        return
      }

      if (res) setIsSuccess(true)
    },
    (errors) => console.warn(errors)
  )

  // Success state - password has been reset
  if (isSuccess) {
    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-[22px] font-light text-new-company-gray-500">
          Welcome to{' '}
          <span className="text-new-company-green-dark font-semibold">
            First Bite
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-new-company-pistachio-light flex items-center justify-center mb-4">
            <HiOutlineCheckCircle className="w-10 h-10 text-new-company-green-dark" />
          </div>

          <h1 className="text-[20px] font-semibold mb-2">
            Password updated successfully
          </h1>

          <p className="text-gray-600 mb-6">
            Your password has been changed. You can now log in with your new
            password.
          </p>

          <FbButton
            onClick={() => (window.location.href = '/login')}
            className="w-full"
          >
            Go to Login
          </FbButton>
        </div>
      </div>
    )
  }

  // Error state - token is invalid or expired
  if (isTokenError) {
    return (
      <div className="w-96 flex-shrink-0">
        <div className="text-[22px] font-light text-new-company-gray-500">
          Welcome to{' '}
          <span className="text-new-company-green-dark font-semibold">
            First Bite
          </span>
        </div>

        <div className="mt-8 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <HiOutlineExclamationCircle className="w-10 h-10 text-red-500" />
          </div>

          <h1 className="text-[20px] font-semibold mb-2">
            Reset link expired or invalid
          </h1>

          <p className="text-gray-600 mb-6">
            This password reset link is no longer valid. Please request a new
            one to reset your password.
          </p>

          <div className="flex flex-col gap-3 w-full">
            <FbButton
              onClick={() => (window.location.href = '/reset-password')}
              className="w-full"
            >
              Request New Link
            </FbButton>

            <FbLink to="/login" className="text-center">
              Back to login
            </FbLink>
          </div>
        </div>
      </div>
    )
  }

  // Form state - enter new password
  return (
    <div className="w-96 flex-shrink-0">
      <div className="text-[22px] font-light text-new-company-gray-500">
        Welcome to{' '}
        <span className="text-new-company-green-dark font-semibold">
          First Bite
        </span>
      </div>
      <h1 className="text-[18px] mb-2 mt-4">Create New Password</h1>
      <p className="text-gray-600 mb-4">
        Choose a new password for your account.
      </p>
      <TextInput
        type="password"
        control={control}
        name="password1"
        label="New Password"
        helperText="Must be at least 8 characters"
      />
      <TextInput
        type="password"
        control={control}
        name="password2"
        label="Confirm New Password"
      />
      <FbButton
        type="submit"
        onClick={onSubmit}
        loading={isSubmitting}
        disabled={!!errors.password2 || !!errors.password1}
        className="w-full mt-4"
      >
        Reset Password
      </FbButton>
      <div className="mt-4 text-center">
        <FbLink to="/login">Back to login</FbLink>
      </div>
    </div>
  )
}

export default ResetPasswordConfirm
